import { Package } from 'lucide-react';
import { useState, type HTMLAttributeReferrerPolicy } from 'react';

import { CopyIconButton } from '~/components/common/copy-icon-button';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';

import type { ProductIdentity } from '../../hooks/use-product-identities';
import { ShortId } from '../ui-kit/short-id';

/**
 * A rounded product thumbnail; a neutral box with a package icon when there is no image or
 * it fails to load. Decorative (empty alt) — the title beside it names the product.
 */
export function ProductThumbnail({
  src,
  className,
  referrerPolicy,
  testId = 'product-thumbnail',
}: {
  src: string | null | undefined;
  className?: string;
  /** Set to `no-referrer` for third-party image URLs a backend asks not to leak our origin to. */
  referrerPolicy?: HTMLAttributeReferrerPolicy;
  /** E2E `data-testid` for the thumbnail; the image is `${testId}-image`. */
  testId?: string;
}) {
  // The failure belongs to one url; a new url gets a fresh try.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = Boolean(src) && failedSrc !== src;
  return (
    <span
      data-testid={testId}
      aria-hidden="true"
      className={cn(
        'bg-muted text-muted-foreground flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-md border',
        className,
      )}
    >
      {showImage ? (
        <img
          data-testid={`${testId}-image`}
          src={src ?? undefined}
          alt=""
          loading="lazy"
          decoding="async"
          {...(referrerPolicy !== undefined ? { referrerPolicy } : {})}
          className="size-full object-cover"
          onError={() => {
            setFailedSrc(src ?? null);
          }}
        />
      ) : (
        <Package className="size-4" />
      )}
    </span>
  );
}

/**
 * A product row's identity: thumbnail, title (two lines at most), then the feed id and family.
 * The internal product id is never shown as text when a title or feed id names the product — it
 * stays one click away behind a copy icon; with neither, the short id is the only name there is.
 */
export function ProductIdentityCell({
  productId,
  identity,
  isLoading = false,
  family,
  copyable = true,
  testId = 'product-identity',
}: {
  productId: string;
  identity: ProductIdentity | undefined;
  /** True while the identity is still being read — shows a skeleton line instead of the title. */
  isLoading?: boolean;
  /** The product family label shown after the id. */
  family?: string;
  /** False inside a clickable button, where a nested copy button would be invalid HTML. */
  copyable?: boolean;
  /** E2E `data-testid` root; title/ids derive `${testId}-...`. */
  testId?: string;
}) {
  const title = identity?.title ?? '';
  const feedId = identity?.externalId.trim() ?? '';
  const loading = isLoading && !identity;
  // Nothing else names the product: fall back to its (short) internal id.
  const idOnly = !loading && !title && !feedId;
  return (
    <span data-testid={testId} className="flex min-w-0 items-center gap-3">
      {loading ? (
        <Skeleton className="size-10 shrink-0 rounded-md" />
      ) : (
        <ProductThumbnail testId={`${testId}-thumbnail`} src={identity?.image} />
      )}
      <span className="flex min-w-0 flex-col gap-0.5">
        {loading ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          title && (
            <span
              data-testid={`${testId}-title`}
              title={title}
              className="text-foreground line-clamp-2 text-sm leading-snug font-medium"
            >
              {title}
            </span>
          )
        )}
        <span className="text-muted-foreground flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs">
          {idOnly &&
            (copyable ? (
              <ShortId
                testId={`${testId}-short-id`}
                id={productId}
                label="product id"
                head={12}
                tail={6}
              />
            ) : (
              <span
                data-testid={`${testId}-product-id`}
                title={productId}
                className="text-foreground truncate font-mono"
              >
                {productId}
              </span>
            ))}
          {feedId && (
            <span
              data-testid={`${testId}-feed-id`}
              title={`Feed id ${feedId}`}
              className="max-w-48 truncate font-mono"
            >
              {feedId}
            </span>
          )}
          {family && (
            <>
              {(idOnly || feedId) && <span aria-hidden="true">·</span>}
              <span data-testid={`${testId}-family`}>{family}</span>
            </>
          )}
          {!idOnly && copyable && (
            <CopyIconButton
              testId={`${testId}-copy-button`}
              text={productId}
              label="Copy product id"
              copyMessage="Product id copied"
              className="size-5"
            />
          )}
        </span>
      </span>
    </span>
  );
}
